/**
 * Role-Based Route Component
 * Wraps routes that require specific roles
 * @module components/guards/RoleBasedRoute
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { getDefaultRoute, hasRouteAccess } from './routes';
import { isTeamLeader, normalizeRole } from '@/utils/roleUtils';

/**
 * RoleBasedRoute - Ensures user has required role before rendering
 * @param {Object} props - Component props
 * @param {React.Component} props.children - Child components to render
 * @param {Object} props.user - Current user object
 * @param {Array<string>} props.allowedRoles - Array of allowed roles
 * @param {string} props.fallbackPath - Path to redirect if access denied
 * @returns {React.Component} - Route or redirect
 */
const RoleBasedRoute = ({ 
  children, 
  user, 
  allowedRoles = [], 
  fallbackPath = null,
  path = null,
}) => {
  // If no user, redirect to login
  if (!user) {
    return <Navigate to="/login" replace />;
  }

  const normalizedRole = normalizeRole(user.role);
  if (path) {
    if (!hasRouteAccess(path, user)) {
      const defaultRoute = fallbackPath || getDefaultRoute(normalizedRole);
      return <Navigate to={defaultRoute} replace />;
    }
    return children;
  }

  if (!allowedRoles || allowedRoles.length === 0) {
    return children;
  }

  const allowed = allowedRoles.includes(normalizedRole)
    || (isTeamLeader(normalizedRole) && allowedRoles.includes('employee'));

  if (!allowed) {
    const defaultRoute = fallbackPath || getDefaultRoute(normalizedRole);
    return <Navigate to={defaultRoute} replace />;
  }

  return children;
};

export default RoleBasedRoute;

